import { useState } from 'react'
import { Plus, Trash2, Users, User as UserIcon } from 'lucide-react'
import {
  type StructureData, type Group, type User, type GroupType,
  GROUP_TYPES, GROUP_TYPE_LABELS, fullGroupName, groupOuPath,
} from '../types.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  Card,
  EmptyState,
  IconButton,
  Input,
  Select,
} from '@/components/ui'
import { cn } from '@/lib/utils.ts'

interface Props {
  data: StructureData
  update: (patch: Partial<StructureData>) => void
}

const GROUP_TYPE_OPTIONS = GROUP_TYPES.map((t) => ({ value: t, label: t }))
const GROUP_TYPE_OPTIONS_WITH_LABEL = GROUP_TYPES.map((t) => ({
  value: t,
  label: `${t} – ${GROUP_TYPE_LABELS[t]}`,
}))

export function GroupsUsersTab({ data, update }: Props) {
  const a = useAnonymize()
  const [newGroupType, setNewGroupType] = useState<GroupType>('AB')
  const [newGroupName, setNewGroupName] = useState('')
  const [newUsername, setNewUsername] = useState('')

  function addGroup() {
    if (!newGroupName.trim()) return
    const g: Group = {
      id: crypto.randomUUID(),
      type: newGroupType,
      name: newGroupName.trim(),
      scope: 'global',
    }
    update({ groups: [...data.groups, g] })
    setNewGroupName('')
  }

  function updateGroup(id: string, patch: Partial<Group>) {
    update({ groups: data.groups.map((g) => (g.id === id ? { ...g, ...patch } : g)) })
  }

  function deleteGroup(id: string) {
    update({
      groups: data.groups
        .filter((g) => g.id !== id)
        .map((g) => {
          if (!g.memberOfGroupIds?.includes(id)) return g
          const next = g.memberOfGroupIds.filter((gid) => gid !== id)
          return { ...g, memberOfGroupIds: next.length > 0 ? next : undefined }
        }),
      users: data.users.map((u) => ({ ...u, groupIds: u.groupIds.filter((gid) => gid !== id) })),
      permissions: data.permissions.filter((p) => p.groupId !== id),
    })
  }

  function addUser() {
    if (!newUsername.trim()) return
    const u: User = {
      id: crypto.randomUUID(),
      username: newUsername.trim(),
      groupIds: [],
    }
    update({ users: [...data.users, u] })
    setNewUsername('')
  }

  function updateUser(id: string, patch: Partial<User>) {
    update({ users: data.users.map((u) => (u.id === id ? { ...u, ...patch } : u)) })
  }

  function deleteUser(id: string) {
    update({ users: data.users.filter((u) => u.id !== id) })
  }

  function toggleGroupForUser(userId: string, groupId: string) {
    const u = data.users.find((x) => x.id === userId)
    if (!u) return
    const groupIds = u.groupIds.includes(groupId)
      ? u.groupIds.filter((g) => g !== groupId)
      : [...u.groupIds, groupId]
    updateUser(userId, { groupIds })
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <Card.Header>
          <Card.Title className="text-body flex items-center gap-2">
            <Users className="w-4 h-4 text-purple-500" />Gruppen
          </Card.Title>
        </Card.Header>
        {data.groups.length === 0 ? (
          <EmptyState size="sm" title="Noch keine Gruppen" />
        ) : (
          <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
            {data.groups.map((g) => (
              <div key={g.id} className="px-4 py-2.5 space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-20 shrink-0">
                    <Select
                      value={g.type}
                      onValueChange={(v) => updateGroup(g.id, { type: v as GroupType })}
                      options={GROUP_TYPE_OPTIONS}
                      triggerClassName="text-caption font-mono py-1"
                    />
                  </div>
                  <Input
                    type="text"
                    wrapperClassName="flex-1"
                    value={a.token(g.name)}
                    onChange={(e) => {
                      if (!a.enabled) updateGroup(g.id, { name: e.target.value })
                    }}
                    readOnly={a.enabled}
                    placeholder="AD-Name (normalisiert)"
                    className="font-mono py-1"
                  />
                  <IconButton
                    icon={<Trash2 className="w-4 h-4" />}
                    aria-label="Gruppe löschen"
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteGroup(g.id)}
                  />
                </div>
                {g.type === 'AB' && (
                  <div className="pl-[92px] flex items-center gap-2">
                    <label className="text-caption text-gray-400 font-medium whitespace-nowrap">
                      Team-Name (Excel):
                    </label>
                    <Input
                      type="text"
                      wrapperClassName="flex-1"
                      value={a.token(g.teamName ?? '')}
                      onChange={(e) => {
                        if (!a.enabled) updateGroup(g.id, { teamName: e.target.value })
                      }}
                      readOnly={a.enabled}
                      placeholder="z.B. Führungsebene (mit Umlauten)"
                      className="text-caption py-0.5"
                      title="Roher Team-Name wie er in der Excel-Berechtigungsmatrix steht (mit Umlauten, Sonderzeichen). Wird beim Excel-Import für das Matching verwendet."
                    />
                  </div>
                )}
                <div className="pl-[92px] text-caption text-gray-500 font-mono space-y-0.5">
                  <p>
                    <span className="text-gray-400">AD-Name:</span>{' '}
                    {a.token(fullGroupName(data.domainPrefix, g.type, g.name))}
                  </p>
                  <p>
                    <span className="text-gray-400">OU:</span>{' '}
                    {a.token(groupOuPath(data.domainPrefix, g.type, data.domain))}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        <Card.Footer className="grid grid-cols-[110px_1fr_auto] gap-2">
          <Select
            value={newGroupType}
            onValueChange={(v) => setNewGroupType(v as GroupType)}
            options={GROUP_TYPE_OPTIONS_WITH_LABEL}
            triggerClassName="text-caption font-mono py-1.5"
          />
          <Input
            type="text"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addGroup()}
            placeholder="Gruppen-Suffix (z.B. IT-Admins)"
            className="py-1.5"
          />
          <IconButton
            icon={<Plus className="w-4 h-4" />}
            aria-label="Gruppe hinzufügen"
            variant="primary"
            size="sm"
            onClick={addGroup}
          />
        </Card.Footer>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title className="text-body flex items-center gap-2">
            <UserIcon className="w-4 h-4 text-blue-500" />Benutzer
          </Card.Title>
        </Card.Header>
        {data.users.length === 0 ? (
          <EmptyState size="sm" title="Noch keine Benutzer" />
        ) : (
          <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
            {data.users.map((u) => (
              <div key={u.id} className="px-4 py-2.5 space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    wrapperClassName="flex-1"
                    value={a.token(u.username)}
                    onChange={(e) => {
                      if (!a.enabled) updateUser(u.id, { username: e.target.value })
                    }}
                    readOnly={a.enabled}
                    className="font-mono py-1"
                  />
                  <IconButton
                    icon={<Trash2 className="w-4 h-4" />}
                    aria-label="Benutzer löschen"
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteUser(u.id)}
                  />
                </div>
                {data.groups.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {data.groups.map((g) => {
                      const checked = u.groupIds.includes(g.id)
                      return (
                        <button
                          key={g.id}
                          onClick={() => toggleGroupForUser(u.id, g.id)}
                          className={cn(
                            'text-caption px-2 py-0.5 rounded font-mono transition-colors border',
                            checked
                              ? 'bg-brand-100 text-brand-700 border-brand-300'
                              : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100',
                          )}
                        >
                          {g.type}_{a.token(g.name)}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <Card.Footer className="grid grid-cols-[1fr_auto] gap-2">
          <Input
            type="text"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addUser()}
            placeholder="Benutzername (z.B. svc-admin)"
            className="font-mono py-1.5"
          />
          <IconButton
            icon={<Plus className="w-4 h-4" />}
            aria-label="Benutzer hinzufügen"
            variant="primary"
            size="sm"
            onClick={addUser}
          />
        </Card.Footer>
      </Card>
    </div>
  )
}
